'use client';
import { CurationsIcon } from '@c/icons';
import SafeImage from '@ui/SafeImage';
import type { ProductLight } from 'models/product';
import { useState } from 'react';
import { MXLockerLogoBadge } from './MXLockerLogoBadge';
import { ProductAuctionCountdown } from './ProductAuctionCountdown';
import { ProductVariationImages } from './ProductVariationImages';
import { SoldOutOverlay } from './SoldOutOverlay';

export const ProductPicture = ({
  product,
  priority,
}: {
  product: ProductLight;
  priority?: boolean;
}) => {
  const [activeImageIndex, setActiveImageIndex] = useState<number | undefined>(
    undefined
  );
  const [hoveredImageIndex, setHoveredImageIndex] = useState<number | null>(
    null
  );
  const [isLoadingHoverImage, setIsLoadingHoverImage] = useState(false);
  const endTime = product.end_time ?? 0;

  const displayIndex = hoveredImageIndex ?? activeImageIndex;
  const displayImage =
    displayIndex !== undefined && displayIndex !== null
      ? product?.variation_images?.[displayIndex] || product.thumbnail
      : product.thumbnail;

  const isMXLockerDirect = product.seller_id === 'E16WuBcn0UMejlp0FSdQulSSed12';
  const isIntegrationProduct = !!product.integration_info;
  const containerBgClass = isMXLockerDirect ? 'bg-white' : 'bg-[#F4F4F4]';
  const imageFitClass = isIntegrationProduct
    ? 'object-contain'
    : 'object-cover';

  return (
    <div
      className={`relative h-[28rem] w-full max-w-full overflow-hidden rounded-[3rem] sm:h-[30rem] ${containerBgClass}`}
    >
      <SafeImage
        key={displayImage}
        alt={product.title}
        src={displayImage}
        // fallbackSrc={product.images[0]?.download_url}
        fill
        priority={priority}
        onLoad={() => setIsLoadingHoverImage(false)}
        classes={`${imageFitClass} h-full w-full max-w-full transition-all duration-100 ease-linear rounded-[3rem] group-hover:scale-105`}
      />

      {isLoadingHoverImage && hoveredImageIndex !== null && (
        <div className="absolute inset-0 animate-pulse rounded-[2rem] bg-zinc-100" />
      )}

      {isMXLockerDirect && <MXLockerLogoBadge />}

      {product.from_recommend && (
        <div className="absolute  left-[1rem] top-[0.8rem] flex w-[4rem] gap-4 overflow-x-hidden whitespace-nowrap rounded-[0.4rem] bg-brand-secondary  px-4 py-[0.8rem]  font-semibold text-white transition-all hover:w-[10rem] ">
          <span className="grow">
            <CurationsIcon width={20} height={20} />
          </span>
          For you
        </div>
      )}

      {product.out_of_stock && !product.is_auction && <SoldOutOverlay />}

      {product.is_auction && endTime > Date.now() && !product.is_draft && (
        <ProductAuctionCountdown product={product} />
      )}

      <div className="z-5 absolute bottom-[0.8rem] left-[0.8rem] right-[0.8rem]">
        <ProductVariationImages
          product={product}
          activeImageIndex={activeImageIndex}
          onSelect={(index, e) => {
            e.preventDefault();
            e.stopPropagation();
            setActiveImageIndex(index);
          }}
          onHover={(index) => {
            setHoveredImageIndex(index);
            if (index !== null) {
              setIsLoadingHoverImage(true);
            }
          }}
        />
      </div>
    </div>
  );
};
